import React from 'react';
import { renderToString } from 'react-dom/server';
import { pick } from 'lodash';

import { humanize, FieldType } from '@zapier/editor-core';

import Link from 'app/common/components/Link';
import { renderMarkdown } from 'app/developer-v3/utils';

import type {
  FieldOrFunctionSchema,
  InputFieldSchema,
} from 'app/developer-v3/platformSchema/field';

const fieldTypes = {
  bool: 'bool' as const,
  copy: 'copy' as const,
  dict: 'dict' as const,
  fields: 'fields' as const,
  list: 'list' as const,
  prettySelect: 'pretty-select' as const,
};

export function getDynamicDropdownCopy(name: string, zapUrl: string) {
  return renderToString(
    <p>
      The preview doesn’t use live data, so the <strong>{name}</strong> dropdown
      will be empty here.{' '}
      <Link href={zapUrl} target="_blank" weight="bold">
        Create a Zap
      </Link>{' '}
      to test the dropdown.
    </p>
  );
}

// TODO: We don't enforce key uniqueness in line-items. Add the index
// here to prevent React key errors.
function replaceKeyWithKeyedIndex(field, index) {
  return { ...field, key: `${field.key}-${index}` };
}

export function convertField(
  zapUrl: string,
  schemaField: InputFieldSchema
): FieldType {
  const field = pick(schemaField, [
    'key',
    'label',
    'required',
    'type',
  ]) as Partial<FieldType>;

  if (schemaField.children) {
    field.fields = schemaField.children
      .map(childField => convertField(zapUrl, childField))
      .map(replaceKeyWithKeyedIndex);
    field.type = fieldTypes.fields;
  }

  if (schemaField.choices) {
    const { choices } = schemaField;
    field.type = fieldTypes.prettySelect;
    field.options = Array.isArray(choices)
      ? choices
      : Object.entries(choices).map(([value, label]) => ({
          label: String(label),
          value,
        }));
  }

  if (schemaField.dict) {
    field.type = fieldTypes.dict;
  }

  if (schemaField.type === 'boolean') {
    field.type = fieldTypes.bool;
  }

  // Only an issue with line-item child fields
  if (!field.label) {
    field.label = humanize(field.key);
  }

  if (schemaField.list) {
    field.type = fieldTypes.list;

    if (field.options) {
      field.listItemType = 'pretty-select';
    }
  }

  if (schemaField.helpText) {
    field.help_text_html = renderMarkdown(schemaField.helpText);
  }

  // We don't call triggers that drive dynamic dropdowns (prefill in the editor).
  // Instead we render a copy field instead of a broken dropdown.
  if (schemaField.dynamic) {
    field.type = fieldTypes.copy;
    field.help_text_html = getDynamicDropdownCopy(field.label, zapUrl);

    delete field.help_text;
  }

  return field as FieldType;
}

// TODO: We should render a copy field for dynamic fields
function removeDynamicFields(
  fields: Array<FieldOrFunctionSchema>
): Array<InputFieldSchema> {
  return fields.filter(
    (field): field is InputFieldSchema => !('source' in field)
  );
}

export function convertFormFields(
  zapUrl: string,
  fields: Array<FieldOrFunctionSchema>
): Array<FieldType> {
  return removeDynamicFields(fields).map(field => convertField(zapUrl, field));
}

export function getDefaultValues(fields: Array<FieldOrFunctionSchema>) {
  return fields.reduce((values, field) => {
    if ('default' in field && field.default && 'key' in field) {
      values[field.key] = String(field.default);
    }

    return values;
  }, {});
}
